import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import API_BASE from '../../config/api';

const PRESET_COLORS = [
  '#BF2634', '#E74C3C', '#E67E22',
  '#E5C04B', '#F1C40F',
  '#39FF14', '#27AE60',
  '#2196F3', '#2980B9',
  '#9B59B6', '#8E44AD',
  '#565656',
];

export default function WorkspaceSettings() {
  const { token } = useAuth();
  const { activeWorkspace, fetchWorkspaces } = useWorkspace();

  const [name, setName] = useState(activeWorkspace?.name || '');
  const [color, setColor] = useState(activeWorkspace?.color || '#BF2634');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setName(activeWorkspace?.name || '');
    setColor(activeWorkspace?.color || '#BF2634');
  }, [activeWorkspace?._id]);

  const isDirty = name !== activeWorkspace?.name || color !== activeWorkspace?.color;

  async function handleSave() {
    if (!name.trim()) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await axios.put(
        `${API_BASE}/api/workspaces/${activeWorkspace._id}`,
        { name: name.trim(), color },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchWorkspaces();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: '32px' }}>

      {/* Name */}
      <Section title="Workspace Name">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            style={{
              flex: 1,
              backgroundColor: '#1a1a1a',
              border: '1px solid #3a3a3a',
              borderRadius: '4px',
              padding: '10px 14px',
              color: '#F5F5F5',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 400,
              fontSize: '15px',
              outline: 'none',
            }}
          />
          <SaveButton
            disabled={!isDirty || isSaving || !name.trim()}
            onClick={handleSave}
            isSaving={isSaving}
            saveSuccess={saveSuccess}
          />
        </div>
        {saveError && (
          <div style={{
            marginTop: '8px',
            fontFamily: "'Roboto', sans-serif",
            fontSize: '13px',
            fontStyle: 'italic',
            color: '#BF2634',
          }}>
            {saveError}
          </div>
        )}
      </Section>

      {/* Color */}
      <Section title="Workspace Color">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {PRESET_COLORS.map((c) => (
            <ColorSwatch
              key={c}
              color={c}
              selected={color === c}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
          <div
            style={{
              width: '28px', height: '28px', borderRadius: '50%',
              backgroundColor: color,
              boxShadow: `0 0 10px ${color}99`,
              flexShrink: 0,
            }}
          />
          <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#888888' }}>
            {color}
          </span>
          {color !== activeWorkspace?.color && (
            <SaveButton
              disabled={isSaving}
              onClick={handleSave}
              isSaving={isSaving}
              saveSuccess={saveSuccess}
            />
          )}
        </div>
      </Section>

      {/* Logo */}
      <Section title="Workspace Logo">
        <div
          style={{
            border: '1px dashed #3a3a3a',
            borderRadius: '8px',
            padding: '28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#565656' }}>
            Logo upload coming soon
          </span>
          <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#444444' }}>
            Square image, min 200 × 200 px recommended
          </span>
        </div>
      </Section>

      {/* Danger Zone */}
      <Section title="Danger Zone" danger>
        {!showDeleteConfirm ? (
          <div
            style={{
              border: '1px solid rgba(191,38,52,0.3)',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
            }}
          >
            <div>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', color: '#F5F5F5' }}>
                Delete this workspace
              </div>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#888888', marginTop: '3px' }}>
                Permanent. All data will be removed and cannot be recovered.
              </div>
            </div>
            <DangerBtn onClick={() => setShowDeleteConfirm(true)}>
              Delete Workspace
            </DangerBtn>
          </div>
        ) : (
          <div
            style={{
              border: '1px solid #BF2634',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5' }}>
              Type{' '}
              <strong style={{ color: '#BF2634' }}>{activeWorkspace?.name}</strong>
              {' '}to confirm:
            </div>
            <input
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder={activeWorkspace?.name}
              style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #BF2634',
                borderRadius: '4px',
                padding: '10px 14px',
                color: '#F5F5F5',
                fontFamily: "'Roboto', sans-serif",
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }}
                style={{
                  background: 'transparent',
                  border: '1px solid #565656',
                  color: '#F5F5F5',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontSize: '13px',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                disabled={deleteInput !== activeWorkspace?.name}
                onClick={() => console.log('DELETE_WORKSPACE', activeWorkspace._id)}
                style={{
                  backgroundColor: deleteInput === activeWorkspace?.name ? '#BF2634' : '#2a2a2a',
                  border: `1px solid ${deleteInput === activeWorkspace?.name ? '#BF2634' : '#3a3a3a'}`,
                  color: deleteInput === activeWorkspace?.name ? '#FFFFFF' : '#565656',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontSize: '13px',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: deleteInput === activeWorkspace?.name ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                }}
              >
                Permanently Delete
              </button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children, danger }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 600,
          fontSize: '12px',
          color: danger ? '#BF2634' : '#888888',
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          marginBottom: '12px',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function ColorSwatch({ color, selected, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '28px', height: '28px', borderRadius: '50%',
        backgroundColor: color,
        cursor: 'pointer',
        border: selected ? '2px solid #FFFFFF' : '2px solid transparent',
        boxShadow: selected
          ? `0 0 0 2px ${color}, 0 0 12px ${color}88`
          : hovered ? `0 0 8px ${color}77` : 'none',
        transition: 'all 0.15s ease',
      }}
    />
  );
}

function SaveButton({ disabled, onClick, isSaving, saveSuccess }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: saveSuccess ? 'rgba(57,255,20,0.12)' : 'transparent',
        border: `1px solid ${
          saveSuccess ? '#39FF14'
          : disabled ? '#2a2a2a'
          : hovered ? '#F5F5F5'
          : '#565656'
        }`,
        color: saveSuccess ? '#39FF14' : disabled ? '#3a3a3a' : '#F5F5F5',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        padding: '10px 18px',
        borderRadius: '4px',
        cursor: disabled ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s ease',
        flexShrink: 0,
      }}
    >
      {isSaving ? 'Saving...' : saveSuccess ? 'Saved ✓' : 'Save Changes'}
    </button>
  );
}

function DangerBtn({ onClick, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? '#BF2634' : 'transparent',
        border: '1px solid #BF2634',
        color: hovered ? '#FFFFFF' : '#BF2634',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}
